import { VendorBillSearch, VendorBillSearchResult } from "@/types/Vendor";
import { adminBaseQueryWrapper } from "@/utils/rtkHelper";
import { createApi } from "@reduxjs/toolkit/query/react";

interface VendorBillResponse {
  order_number: string;
  bill_date: number;
  due_date: number;
  id: number;
}

interface DeletePurchaseParams {
  id: number;
  company_id: string | null;
}

export const vendorBillApi = createApi({
  reducerPath: "vendorBillApi",
  baseQuery: adminBaseQueryWrapper,
  tagTypes: ["vendorBill"],
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    searchVendorBill: builder.query<VendorBillSearchResult, VendorBillSearch>({
      query: (input) => ({
        url: `/vendor-bill/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["vendorBill"],
    }),
    exportVenderBill: builder.mutation<Blob, VendorBillSearch>({
      query: (input) => ({
        url: `/vendor-bill/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    getItemByBillId: builder.query<any, any>({
      query: ({ id, company_id }) =>
        `/vendor-bill/vendor/${id}?company_id=${company_id}`,
      providesTags: ["vendorBill"],
    }),

    getItemPrintById: builder.query<any, any>({
      query: ({ id, company_id }) =>
        `/vendor-bill/vendor_bill_print_info/${id}?company_id=${company_id}`,
      providesTags: ["vendorBill"],
    }),

    deleteItem: builder.mutation<void, DeletePurchaseParams>({
      query: ({ id, company_id }) => ({
        url: `/purchase/${id}?company_id=${company_id}`,
        method: "DELETE",
      }),
    }),
    getVendorBillById: builder.query<any, number>({
      query: (id) => `/vendor-bill/${id}`,
      providesTags: ["vendorBill"],
    }),

    getAllVendorBill: builder.query<VendorBillResponse[], void>({
      query: () => `/vendor-bill`,
      providesTags: ["vendorBill"],
    }),

    createVendorBill: builder.mutation<void, any>({
      query: (input) => ({
        url: `/vendor-bill`,
        method: "POST",
        body: input,
      }),
      invalidatesTags: ["vendorBill"],
    }),
    createVendorBillWithItem: builder.mutation<void, any>({
      query: (input) => ({
        url: `/vendor-bill/create-vendor-bill-with-items`,
        method: "POST",
        body: input,
      }),
      invalidatesTags: ["vendorBill"],
    }),
    updateVendorBill: builder.mutation<void, { id: number; data: any }>({
      query: ({ id, data }) => ({
        url: `/vendor-bill/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["vendorBill"],
    }),
    deleteVendorBill: builder.mutation<void, any>({
      query: ({ id, company_id }) => ({
        url: `/vendor-bill/${id}/?company_id=${company_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["vendorBill"],
    }),
    VenderBillFileUpload: builder.mutation<
      any,
      { file: File; company_id: number }
    >({
      query: ({ file, company_id }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("company_id", company_id.toString());
        return {
          url: `/vendor-bill/upload-bill-file`,
          method: "POST",
          body: formData,
        };
      },
    }),
    VenderBillFileValidate: builder.query({
      query: ({ document_id, company_id }) => ({
        url: `/vendor-bill/validate-bill-file`,
        params: { document_id, company_id },
      }),
    }),
    VenderBillFileValidateJson: builder.mutation({
      query: ({ records }) => ({
        url: `/vendor-bill/validate-records`,
        method: "POST",
        body: { records },
      }),
    }),
    confirmVendorFileUpload: builder.mutation<any, { records: any[] }>({
      query: (body) => ({
        url: `/vendor-bill/confirm-bill-upload`,
        method: "POST",
        body,
      }),
    }),
    generateVendorExcel: builder.query<Blob, void>({
      query: () => ({
        url: `/vendor-bill/bill/generate-vendor-excel`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetAllVendorBillQuery,
  useCreateVendorBillMutation,
  useUpdateVendorBillMutation,
  useSearchVendorBillQuery,
  useLazySearchVendorBillQuery,
  useExportVenderBillMutation,
  useGetVendorBillByIdQuery,
  useCreateVendorBillWithItemMutation,
  useGetItemByBillIdQuery,
  useLazyGetItemByBillIdQuery,
  useDeleteItemMutation,
  useGetItemPrintByIdQuery,
  useDeleteVendorBillMutation,
  useVenderBillFileUploadMutation,
  useLazyVenderBillFileValidateQuery,
  useVenderBillFileValidateJsonMutation,
  useConfirmVendorFileUploadMutation,
  useLazyGenerateVendorExcelQuery,
} = vendorBillApi;
